/**
 * O núcleo DOM dos componentes (F111): `h()` cria um elemento com atributos, ouvintes (`on<evento>`) e
 * filhos. Texto entra sempre como nó de texto, nunca `innerHTML`: apelidos, mensagens da API e nomes
 * de arquivos importados não viram marcação. Estilo dinâmico só pelo CSSOM (`style.setProperty`): a
 * CSP das páginas (`style-src 'self'`) recusa o atributo `style` escrito no HTML.
 */
export type Child = Node | string | number | false | null | undefined;
export type AttrValue = string | number | boolean | null | undefined | ((event: Event) => void);
export type Attrs = Record<string, AttrValue>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  setAttrs(el, attrs);
  append(el, ...children);
  return el;
}

/**
 * Atributos e ouvintes. `on<evento>` (em qualquer caixa: `onclick`, `onClick`) vira ouvinte. Nos
 * `aria-*`, `true` e `false` viram "true" e "false" (o `aria-hidden=""` vazio valeria "não oculto");
 * nos demais, `false`, `null` e `undefined` ficam de fora e `true` vira atributo vazio.
 */
export function setAttrs(el: Element, attrs: Attrs): void {
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "function") {
      el.addEventListener(k.slice(2).toLowerCase(), v);
      continue;
    }
    if (k.startsWith("aria-") && typeof v === "boolean") {
      el.setAttribute(k, String(v));
      continue;
    }
    if (v === false) continue;
    el.setAttribute(k, v === true ? "" : String(v));
  }
}

export function append(el: Element, ...children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

/** Junta classes, pulando as vazias (`cx("btn", primary && "btn-primary")`). */
export const cx = (...parts: (string | false | null | undefined)[]): string => parts.filter(Boolean).join(" ");

let counter = 0;
/** Um id único na página, para ligar rótulo, dica e erro (`aria-describedby`, `aria-controls`). */
export const uid = (prefix = "ui"): string => `${prefix}-${(++counter).toString(36)}`;
